export type Lang = "sv" | "en";

export const LANGUAGES: { code: Lang; label: string; flag: string }[] = [
  { code: "sv", label: "Svenska", flag: "🇸🇪" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

// Translation dictionaries. Keys use dot-notation by convention but are
// just plain strings in a flat record. Use {var} for interpolation.
const sv = {
  // App / Header
  "app.title": "Tidrapportering",
  "app.subtitle": "Agria vet guide AB",
  "app.loading": "Laddar…",
  "app.signOut": "Logga ut",

  // Roles
  "role.admin": "Admin",
  "role.manager": "Chef",
  "role.employee": "Anställd",

  // Navigation
  "nav.myMonth": "Min månad",
  "nav.history": "Historik",
  "nav.team": "Mitt team",
  "nav.allReports": "Alla rapporter",
  "nav.admin": "Admin",

  // Login
  "login.subtitle":
    "Logga in med ditt Agria-konto för att rapportera dina avvikelser för månaden.",
  "login.access": "Endast medarbetare på Agria vet guide AB har tillgång.",
  "login.signingIn": "Loggar in…",
  "login.signInFailed": "Inloggning misslyckades",
  "login.dev.title": "Lokal dev-inloggning",
  "login.dev.help": "DEV_AUTH_BYPASS är aktiverad. Hoppar över Google. Stäng av i prod.",
  "login.dev.email": "E-post",
  "login.dev.name": "Namn (visas i UI)",
  "login.dev.submit": "Logga in (dev-läge)",
  "login.dev.submitting": "Loggar in…",
  "login.google.missing":
    "VITE_GOOGLE_CLIENT_ID saknas. Sätt det i {file} för att aktivera Google-inloggning, eller använd dev-inloggningen nedan.",

  // Common
  "common.cancel": "Avbryt",
  "common.save": "Spara",
  "common.add": "Lägg till",
  "common.close": "Stäng",
  "common.unknown": "Okänd",
  "common.something_went_wrong": "Något gick fel",
  "common.session_expired": "Sessionen har gått ut. Logga in igen.",

  // Onboarding
  "onboarding.title": "Hej och välkommen!",
  "onboarding.body":
    "Du är inloggad men saknar fortfarande ett 6-siffrigt arbetstagar-ID. Be administratören (Marcus eller Filippa) att tilldela dig ett ID och en chef. Tills dess kan du inte skicka in tidrapporter.",

  // Status labels
  "status.draft": "Utkast",
  "status.submitted": "Inskickad – väntar på chef",
  "status.manager_approved": "Godkänd av chef – väntar på VD",
  "status.admin_approved": "Godkänd av VD",
  "status.rejected": "Avvisad",

  // Reporting page
  "reporting.subtitle":
    "Rapportera in dina avvikelser för månaden — semester, sjukdom, VAB osv. Hade du inga avvikelser? Bocka i \"Inga avvikelser\" och skicka in.",
  "reporting.locked":
    "Den här månaden är låst. Du kan bara redigera innevarande och föregående månad.",
  "reporting.status": "Status",
  "reporting.submittedAt": "Inskickad {date}",
  "reporting.rejected": "Avvisad:",
  "reporting.noDevs.checked": "✓ Inga avvikelser",
  "reporting.noDevs.mark": "Markera \"Inga avvikelser\"",
  "reporting.noDevs.disableHint":
    "Ta bort alla avvikelser för att markera detta",
  "reporting.submit": "Skicka in månaden",
  "reporting.submitting": "Skickar…",
  "reporting.recall": "Dra tillbaka",
  "reporting.deviations": "Avvikelser",
  "reporting.deviationsHelp":
    "Lägg till en rad per period (t.ex. semester 5–9 maj). Verktyget räknar automatiskt ut antalet arbetsdagar och hoppar över röda dagar.",
  "reporting.addDeviation": "+ Lägg till avvikelse",
  "reporting.empty.noDeviations": "Inga avvikelser för månaden.",
  "reporting.empty.notAdded": "Inga avvikelser tillagda än.",
  "reporting.delete": "Ta bort",

  // History
  "history.title": "Historik",
  "history.subtitle": "Alla månader du har rapporterat in.",
  "history.empty": "Du har inte rapporterat någon månad än.",
  "history.noDeviations": "Inga avvikelser",
  "history.deviation_one": "{count} avvikelse",
  "history.deviation_many": "{count} avvikelser",
  "history.submittedAt": "Inskickad {date}",

  // Team page
  "team.title.admin": "Alla rapporter",
  "team.title.manager": "Mitt team",
  "team.subtitle.admin": "Slutgodkänn alla rapporter innan du exporterar Excel-filen.",
  "team.subtitle.manager": "Granska och godkänn rapporter från personerna du är chef för.",
  "team.stat.draft": "Utkast",
  "team.stat.submitted": "Inskickade",
  "team.stat.approved": "Godkända",
  "team.stat.rejected": "Avvisade",
  "team.empty": "Inga rapporter att visa.",
  "team.missingEmployeeId": "Saknar arbetstagar-ID",
  "team.deviations_count": "{count} avvikelser",
  "team.detail.empty": "Välj en rapport för att se detaljer.",
  "team.detail.employeeId": " · Arbtagid {id}",
  "team.detail.missingEmployeeId": " · Saknar arbetstagar-ID",
  "team.detail.markedNoDeviations": "Markerad som",
  "team.detail.noDeviationsLabel": "Inga avvikelser",
  "team.detail.forMonth": "för månaden.",
  "team.detail.notReported": "Personen har inte rapporterat in något än.",
  "team.detail.rejected": "Avvisad:",
  "team.detail.approveManager": "Godkänn (chef)",
  "team.detail.approveAdmin": "Slutgodkänn",
  "team.detail.rejectToggle": "Avvisa rapport",
  "team.detail.rejectReason": "Anledning till avvisning",
  "team.detail.rejectButton": "Avvisa",

  // Admin page
  "admin.title": "Administration",
  "admin.subtitle":
    "Hantera anställda, lönearter och exportera Excel-filer för lönecentret.",
  "admin.tab.users": "Anställda",
  "admin.tab.wageCodes": "Lönearter",
  "admin.tab.export": "Excel-export",

  // Admin / users
  "admin.users.search": "Sök på namn, email eller arbetstagar-ID",
  "admin.users.countOf": "{filtered} av {total}",
  "admin.users.add": "+ Lägg till anställd",
  "admin.users.notLinked": "Ej kopplad till mailadress",
  "admin.users.linkEmail": "Koppla mailadress",
  "admin.users.linkEmailSave": "Koppla",
  "admin.users.empty": "Inga matchande anställda.",
  "admin.users.col.name": "Namn / Email",
  "admin.users.col.employeeId": "Arbetstagar-ID",
  "admin.users.col.role": "Roll",
  "admin.users.col.manager": "Chef",
  "admin.users.col.active": "Aktiv",
  "admin.users.col.actions": "Åtgärder",
  "admin.users.employeeIdPlaceholder": "6 siffror",
  "admin.users.noManager": "— Ingen chef —",
  "admin.users.addTitle": "Lägg till ny anställd",
  "admin.users.addHelp":
    "Fyll i Google-mailen som personen kommer logga in med. När personen sedan loggar in via Google första gången får de direkt åtkomst.",
  "admin.users.field.email": "Email",
  "admin.users.field.emailPh": "namn@agriavetguide.com",
  "admin.users.field.name": "Namn",
  "admin.users.field.namePh": "För- och efternamn",
  "admin.users.field.employeeId": "Arbetstagar-ID (6 siffror)",
  "admin.users.field.employeeIdPh": "t.ex. 113075",
  "admin.users.field.role": "Roll",
  "admin.users.field.manager": "Chef",
  "admin.users.delete": "Ta bort",
  "admin.users.deleteTitle": "Ta bort {name}?",
  "admin.users.deleteBody":
    "Användaren kommer tas bort permanent. Detta går bara om personen inte har skickat in några rapporter, godkänt några rapporter eller exporterat någon Excel. Om personen har historik — avaktivera istället.",
  "admin.users.deleteConfirm": "Ta bort permanent",

  // Admin / wage codes
  "admin.wageCodes.subtitle":
    "Lönearter som visas i rullgardinen när någon rapporterar tid.",
  "admin.wageCodes.add": "+ Lägg till löneart",
  "admin.wageCodes.col.code": "Kod",
  "admin.wageCodes.col.description": "Beskrivning",
  "admin.wageCodes.col.requiresHours": "Kräver timmar",
  "admin.wageCodes.col.active": "Aktiv",
  "admin.wageCodes.addTitle": "Lägg till löneart",
  "admin.wageCodes.field.code": "Kod (Löneart)",
  "admin.wageCodes.field.codePh": "t.ex. 30f",
  "admin.wageCodes.field.description": "Beskrivning",
  "admin.wageCodes.field.descriptionPh": "t.ex. Övertid helg",
  "admin.wageCodes.field.requiresHours": "Kräver antal timmar",

  // Admin / export
  "admin.export.title": "Exportera Excel",
  "admin.export.subtitle":
    "Generera en Excel-fil i samma format som lönecentrets mall (Inläsningsfil HR+8). Som standard inkluderas bara rapporter som du har slutgodkänt.",
  "admin.export.year": "År",
  "admin.export.month": "Månad",
  "admin.export.includeAll": "Inkludera alla rapporter (även icke-godkända)",
  "admin.export.download": "Ladda ner Excel",
  "admin.export.creating": "Skapar fil…",
  "admin.export.stat.draft": "Utkast",
  "admin.export.stat.pending": "Inskickade",
  "admin.export.stat.managerOk": "Godkända av chef",
  "admin.export.stat.final": "Slutgodkända",

  // Entry form
  "entry.reason": "Anledning (löneart)",
  "entry.hours": "Antal timmar",
  "entry.hoursPh": "t.ex. 4",
  "entry.from": "Från och med",
  "entry.to": "Till och med",
  "entry.workdays_one": "arbetsdag",
  "entry.workdays_many": "arbetsdagar",
  "entry.workdaysSuffix": "(mån–fre, exkl. röda dagar)",
  "entry.calendarDaysTotal": "{count} kalenderdagar totalt",
  "entry.holidaysInRange": "Röda dagar i intervallet:",
  "entry.note": "Anteckning (valfri)",
  "entry.notePh": "t.ex. HR-NUMMER, ärende-id eller annan info till lönecentret",
  "entry.saving": "Sparar…",
  "entry.add": "Lägg till",

  // Month switcher
  "month.prev": "Föregående månad",
  "month.next": "Nästa månad",

  // Months (capitalized)
  "months.1": "Januari",
  "months.2": "Februari",
  "months.3": "Mars",
  "months.4": "April",
  "months.5": "Maj",
  "months.6": "Juni",
  "months.7": "Juli",
  "months.8": "Augusti",
  "months.9": "September",
  "months.10": "Oktober",
  "months.11": "November",
  "months.12": "December",

  // Wage code descriptions (used as overrides for the standard codes;
  // unknown codes fall back to the description from the database)
  "wageCode.NONE": "Inga avvikelser (ingen löneart)",
  "wageCode.200": "Semester",
  "wageCode.424": "Arbetsfri dag",
  "wageCode.425": "Permission",
  "wageCode.420": "VAB",
  "wageCode.40c": "Föräldraledig",
  "wageCode.30f": "Sjuk 100% (eller ange timmar)",
  "wageCode.30w": "Sjuk 75%",
  "wageCode.30y": "Sjuk 50%",
  "wageCode.30z": "Sjuk 25%",
  "wageCode.OT1": "Övertid (helg/incident)",
  "wageCode.OT2": "Mertid (deltid → fulltid)",

  // Language switcher
  "lang.label": "Språk",
  "lang.sv": "Svenska",
  "lang.en": "English",
} as const;

type Key = keyof typeof sv;

const en: Record<Key, string> = {
  // App / Header
  "app.title": "Time reporting",
  "app.subtitle": "Agria vet guide AB",
  "app.loading": "Loading…",
  "app.signOut": "Sign out",

  // Roles
  "role.admin": "Admin",
  "role.manager": "Manager",
  "role.employee": "Employee",

  // Navigation
  "nav.myMonth": "My month",
  "nav.history": "History",
  "nav.team": "My team",
  "nav.allReports": "All reports",
  "nav.admin": "Admin",

  // Login
  "login.subtitle":
    "Sign in with your Agria account to report your deviations for the month.",
  "login.access": "Only employees at Agria vet guide AB have access.",
  "login.signingIn": "Signing in…",
  "login.signInFailed": "Sign in failed",
  "login.dev.title": "Local dev sign in",
  "login.dev.help":
    "DEV_AUTH_BYPASS is enabled. Skipping Google. Disable in production.",
  "login.dev.email": "Email",
  "login.dev.name": "Name (shown in UI)",
  "login.dev.submit": "Sign in (dev mode)",
  "login.dev.submitting": "Signing in…",
  "login.google.missing":
    "VITE_GOOGLE_CLIENT_ID is missing. Set it in {file} to enable Google sign in, or use the dev sign in below.",

  // Common
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.add": "Add",
  "common.close": "Close",
  "common.unknown": "Unknown",
  "common.something_went_wrong": "Something went wrong",
  "common.session_expired": "Your session has expired. Please sign in again.",

  // Onboarding
  "onboarding.title": "Welcome!",
  "onboarding.body":
    "You're signed in but you still don't have a 6-digit employee ID. Ask an administrator (Marcus or Filippa) to assign you an ID and a manager. Until then, you cannot submit time reports.",

  // Status labels
  "status.draft": "Draft",
  "status.submitted": "Submitted – awaiting manager",
  "status.manager_approved": "Approved by manager – awaiting CEO",
  "status.admin_approved": "Approved by CEO",
  "status.rejected": "Rejected",

  // Reporting page
  "reporting.subtitle":
    "Report your deviations for the month — vacation, sick leave, VAB, etc. Had no deviations? Tick \"No deviations\" and submit.",
  "reporting.locked":
    "This month is locked. You can only edit the current and previous month.",
  "reporting.status": "Status",
  "reporting.submittedAt": "Submitted {date}",
  "reporting.rejected": "Rejected:",
  "reporting.noDevs.checked": "✓ No deviations",
  "reporting.noDevs.mark": "Mark \"No deviations\"",
  "reporting.noDevs.disableHint":
    "Remove all deviations to mark this",
  "reporting.submit": "Submit the month",
  "reporting.submitting": "Submitting…",
  "reporting.recall": "Recall",
  "reporting.deviations": "Deviations",
  "reporting.deviationsHelp":
    "Add one row per period (e.g. vacation 5–9 May). The tool automatically counts working days and skips public holidays.",
  "reporting.addDeviation": "+ Add deviation",
  "reporting.empty.noDeviations": "No deviations for the month.",
  "reporting.empty.notAdded": "No deviations added yet.",
  "reporting.delete": "Remove",

  // History
  "history.title": "History",
  "history.subtitle": "All months you have submitted.",
  "history.empty": "You haven't reported any month yet.",
  "history.noDeviations": "No deviations",
  "history.deviation_one": "{count} deviation",
  "history.deviation_many": "{count} deviations",
  "history.submittedAt": "Submitted {date}",

  // Team page
  "team.title.admin": "All reports",
  "team.title.manager": "My team",
  "team.subtitle.admin":
    "Final-approve all reports before exporting the Excel file.",
  "team.subtitle.manager":
    "Review and approve reports from the people you manage.",
  "team.stat.draft": "Draft",
  "team.stat.submitted": "Submitted",
  "team.stat.approved": "Approved",
  "team.stat.rejected": "Rejected",
  "team.empty": "No reports to display.",
  "team.missingEmployeeId": "Missing employee ID",
  "team.deviations_count": "{count} deviations",
  "team.detail.empty": "Select a report to see details.",
  "team.detail.employeeId": " · Employee ID {id}",
  "team.detail.missingEmployeeId": " · Missing employee ID",
  "team.detail.markedNoDeviations": "Marked as",
  "team.detail.noDeviationsLabel": "No deviations",
  "team.detail.forMonth": "for the month.",
  "team.detail.notReported": "This person hasn't reported anything yet.",
  "team.detail.rejected": "Rejected:",
  "team.detail.approveManager": "Approve (manager)",
  "team.detail.approveAdmin": "Final approve",
  "team.detail.rejectToggle": "Reject report",
  "team.detail.rejectReason": "Reason for rejection",
  "team.detail.rejectButton": "Reject",

  // Admin page
  "admin.title": "Administration",
  "admin.subtitle":
    "Manage employees, wage codes and export Excel files for the payroll center.",
  "admin.tab.users": "Employees",
  "admin.tab.wageCodes": "Wage codes",
  "admin.tab.export": "Excel export",

  // Admin / users
  "admin.users.search": "Search by name, email or employee ID",
  "admin.users.countOf": "{filtered} of {total}",
  "admin.users.add": "+ Add employee",
  "admin.users.notLinked": "Not linked to email address",
  "admin.users.linkEmail": "Link email address",
  "admin.users.linkEmailSave": "Link",
  "admin.users.empty": "No matching employees.",
  "admin.users.col.name": "Name / Email",
  "admin.users.col.employeeId": "Employee ID",
  "admin.users.col.role": "Role",
  "admin.users.col.manager": "Manager",
  "admin.users.col.active": "Active",
  "admin.users.col.actions": "Actions",
  "admin.users.employeeIdPlaceholder": "6 digits",
  "admin.users.noManager": "— No manager —",
  "admin.users.addTitle": "Add new employee",
  "admin.users.addHelp":
    "Enter the Google email the person will sign in with. The first time they sign in via Google they'll get access automatically.",
  "admin.users.field.email": "Email",
  "admin.users.field.emailPh": "name@agriavetguide.com",
  "admin.users.field.name": "Name",
  "admin.users.field.namePh": "First and last name",
  "admin.users.field.employeeId": "Employee ID (6 digits)",
  "admin.users.field.employeeIdPh": "e.g. 113075",
  "admin.users.field.role": "Role",
  "admin.users.field.manager": "Manager",
  "admin.users.delete": "Delete",
  "admin.users.deleteTitle": "Delete {name}?",
  "admin.users.deleteBody":
    "The user will be permanently removed. This is only allowed if the person has not submitted any reports, approved any reports or exported any Excel files. If they have history — deactivate instead.",
  "admin.users.deleteConfirm": "Delete permanently",

  // Admin / wage codes
  "admin.wageCodes.subtitle":
    "Wage codes shown in the dropdown when someone reports time.",
  "admin.wageCodes.add": "+ Add wage code",
  "admin.wageCodes.col.code": "Code",
  "admin.wageCodes.col.description": "Description",
  "admin.wageCodes.col.requiresHours": "Requires hours",
  "admin.wageCodes.col.active": "Active",
  "admin.wageCodes.addTitle": "Add wage code",
  "admin.wageCodes.field.code": "Code (Wage code)",
  "admin.wageCodes.field.codePh": "e.g. 30f",
  "admin.wageCodes.field.description": "Description",
  "admin.wageCodes.field.descriptionPh": "e.g. Overtime weekend",
  "admin.wageCodes.field.requiresHours": "Requires number of hours",

  // Admin / export
  "admin.export.title": "Export Excel",
  "admin.export.subtitle":
    "Generate an Excel file in the same format as the payroll template (Inläsningsfil HR+8). By default only reports you have final-approved are included.",
  "admin.export.year": "Year",
  "admin.export.month": "Month",
  "admin.export.includeAll": "Include all reports (also non-approved)",
  "admin.export.download": "Download Excel",
  "admin.export.creating": "Creating file…",
  "admin.export.stat.draft": "Drafts",
  "admin.export.stat.pending": "Submitted",
  "admin.export.stat.managerOk": "Approved by manager",
  "admin.export.stat.final": "Final approved",

  // Entry form
  "entry.reason": "Reason (wage code)",
  "entry.hours": "Number of hours",
  "entry.hoursPh": "e.g. 4",
  "entry.from": "From",
  "entry.to": "To",
  "entry.workdays_one": "working day",
  "entry.workdays_many": "working days",
  "entry.workdaysSuffix": "(Mon–Fri, excl. public holidays)",
  "entry.calendarDaysTotal": "{count} calendar days total",
  "entry.holidaysInRange": "Public holidays in range:",
  "entry.note": "Note (optional)",
  "entry.notePh": "e.g. HR number, ticket id or other info for payroll",
  "entry.saving": "Saving…",
  "entry.add": "Add",

  // Month switcher
  "month.prev": "Previous month",
  "month.next": "Next month",

  // Months (capitalized)
  "months.1": "January",
  "months.2": "February",
  "months.3": "March",
  "months.4": "April",
  "months.5": "May",
  "months.6": "June",
  "months.7": "July",
  "months.8": "August",
  "months.9": "September",
  "months.10": "October",
  "months.11": "November",
  "months.12": "December",

  // Wage code descriptions
  "wageCode.NONE": "No deviations (no wage code)",
  "wageCode.200": "Vacation",
  "wageCode.424": "Day off",
  "wageCode.425": "Leave of absence",
  "wageCode.420": "Care of sick child (VAB)",
  "wageCode.40c": "Parental leave",
  "wageCode.30f": "Sick 100% (or specify hours)",
  "wageCode.30w": "Sick 75%",
  "wageCode.30y": "Sick 50%",
  "wageCode.30z": "Sick 25%",
  "wageCode.OT1": "Overtime (weekend/incident)",
  "wageCode.OT2": "Extra time (part-time → full-time)",

  // Language switcher
  "lang.label": "Language",
  "lang.sv": "Svenska",
  "lang.en": "English",
};

export const translations: Record<Lang, Record<Key, string>> = { sv, en };

export type TranslationKey = Key;

const STORAGE_KEY = "avg.lang";
const DEFAULT_LANG: Lang = "sv";

/** Read currently-selected language from localStorage (without React hooks). */
export function getStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "sv" || stored === "en") return stored;
  } catch {
    // localStorage may be blocked.
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const code = navigator.language.toLowerCase();
    if (code.startsWith("sv")) return "sv";
    if (code.startsWith("en")) return "en";
  }
  return DEFAULT_LANG;
}

/**
 * Translate a key without a React hook. Useful in modules like `api.ts`
 * that run outside the component tree. Falls back to the default language
 * if the stored language doesn't have a translation for the key.
 */
export function tStatic(key: TranslationKey): string {
  const lang = getStoredLang();
  return translations[lang][key] ?? translations[DEFAULT_LANG][key] ?? key;
}
